/**
 * The runtime settings of the server, in groups (spec §8.26, §10.8). Each group is its own form:
 * **Save** sends only the rows that changed, and the server saves all of them or none. **Reset**
 * removes a saved value at once. A secret is write-only: the server only says whether one is set.
 */
import { clsx } from "clsx";
import { ChevronDown, ChevronRight, PlugZap } from "lucide-react";
import { useState } from "react";
import { useI18n, useT } from "../i18n";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { ServerRefusal, useLlmTest, useSaveServerSettings, useServerSettings, type FieldError, type LlmTestResult, type ServerSettings, type SettingRow, type SettingValue } from "./api";
import { readable } from "./format";
import { CHOICE, ERROR, GROUP, GROUP_SUB, SETTING, SETTING_HELP, SOURCE, TEST } from "./labels";

type Draft = Record<string, SettingValue>;

export function SettingsTab() {
  const t = useT();
  const settings = useServerSettings();
  if (settings.isError) return <p className="text-sm text-neutral-500">{t("server.needServer")}</p>;
  if (!settings.data) return null;
  return (
    <div data-testid="server-settings-tab">
      {settings.data.groups.map((g) => (
        <Group key={g.id} id={g.id} rows={g.settings} />
      ))}
      <Environment data={settings.data} />
    </div>
  );
}

function Group({ id, rows }: { id: string; rows: SettingRow[] }) {
  const t = useT();
  const save = useSaveServerSettings();
  const [draft, setDraft] = useState<Draft>({});
  const [errors, setErrors] = useState<Record<string, FieldError>>({});
  const [saved, setSaved] = useState(false);
  // a row is in the draft only while it differs from the saved value (a secret: while one is typed)
  const changed = Object.keys(draft);
  const set = (key: string, value: SettingValue | undefined) => {
    setSaved(false);
    setDraft((d) => {
      const next = { ...d };
      if (value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });
  };
  const submit = () => {
    if (!changed.length) return;
    setErrors({});
    save.mutate(
      { values: Object.fromEntries(changed.map((k) => [k, draft[k]!])) },
      {
        onSuccess: () => {
          setDraft({});
          setSaved(true);
        },
        onError: (e) => setErrors(e instanceof ServerRefusal ? e.errors : { _: { code: "failed", message: String(e) } }),
      },
    );
  };
  const reset = (key: string) => {
    set(key, undefined);
    save.mutate({ reset: [key] });
  };

  return (
    <section className="kss-surface mb-4 p-4" data-testid="server-group" data-group={id}>
      <div className="mb-3 flex items-start gap-3">
        <div className="grow">
          <h2 className="text-[17px] font-semibold">{t(GROUP[id] ?? id)}</h2>
          <p className="text-[13px] text-neutral-500">{t(GROUP_SUB[id] ?? id)}</p>
        </div>
        {(id === "llm" || id === "decision") && <TestConnection target={id} rows={rows} draft={draft} />}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
          {rows.map((r) => (
            <Row key={r.key} row={r} draft={draft[r.key]} error={errors[r.key]} onChange={(v) => set(r.key, v)} onReset={() => reset(r.key)} />
          ))}
        </ul>
        {errors._ && (
          <p className="mt-2 text-sm text-err" role="alert">
            {String(errors._.message ?? "")}
          </p>
        )}
        <div className="mt-3 flex items-center justify-end gap-2">
          {saved && !changed.length && (
            <span className="text-xs text-ok" role="status" data-testid="server-group-saved">
              {t("server.saved")}
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            disabled={!Object.keys(draft).length}
            onClick={() => {
              setDraft({});
              setErrors({});
            }}
            data-testid="server-group-discard"
          >
            {t("server.discard")}
          </Button>
          <Button type="submit" disabled={!changed.length || save.isPending} data-testid="server-group-save">
            {t("server.save")}
          </Button>
        </div>
      </form>
    </section>
  );
}

function Row({ row, draft, error, onChange, onReset }: { row: SettingRow; draft: SettingValue | undefined; error?: FieldError; onChange(v: SettingValue | undefined): void; onReset(): void }) {
  const t = useT();
  const { language } = useI18n();
  const value = draft ?? row.value;
  const hint = typeof value === "number" ? readable(value, row.unit, language) : null;
  return (
    <li className="grid gap-x-4 gap-y-1 py-3 sm:grid-cols-[minmax(0,15rem)_1fr]" data-testid="server-setting" data-key={row.key} data-source={row.source}>
      <div className="min-w-0">
        <div className="text-sm font-medium">{t(SETTING[row.key] ?? row.key)}</div>
        <div className="truncate font-mono text-[11px] text-neutral-500" title={row.key}>
          {row.key}
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Control row={row} value={value} draft={draft} onChange={onChange} />
          {hint && <span className="text-xs text-neutral-500">= {hint}</span>}
          <span className={clsx("kss-pill", row.source === "saved" ? "border-accent text-accent" : "border-neutral-200 text-neutral-500 dark:border-neutral-700")} data-testid="server-setting-source">
            {t(SOURCE[row.source] ?? row.source)}
          </span>
          {row.source === "saved" && (
            <button type="button" className="text-xs text-neutral-500 underline-offset-2 hover:text-neutral-900 hover:underline dark:hover:text-neutral-100" onClick={onReset} data-testid="server-setting-reset">
              {t("server.reset")}
            </button>
          )}
        </div>
        <p className="mt-1 text-[13px] text-neutral-500">{t(SETTING_HELP[row.key] ?? row.key)}</p>
        {row.hidden && (
          <p className="mt-1 text-[13px] text-warn" data-testid="server-setting-hidden">
            {t("server.hidden", { value: row.type === "secret" ? t("server.secret.set") : String(row.base) })}
          </p>
        )}
        {row.unreadable && <p className="mt-1 text-[13px] text-warn">{t("server.secret.unreadable")}</p>}
        {error && (
          <p className="mt-1 text-[13px] text-err" role="alert" data-testid="server-setting-error">
            {errorText(t, error)}
          </p>
        )}
      </div>
    </li>
  );
}

function errorText(t: ReturnType<typeof useT>, e: FieldError): string {
  const params = Object.fromEntries(Object.entries(e).map(([k, v]) => [k, Array.isArray(v) ? v.join(" ") : String(v ?? "")]));
  return t(ERROR[e.code] ?? e.code, params);
}

function Control({ row, value, draft, onChange }: { row: SettingRow; value: SettingValue; draft: SettingValue | undefined; onChange(v: SettingValue | undefined): void }) {
  const t = useT();
  const label = t(SETTING[row.key] ?? row.key);
  const [replacing, setReplacing] = useState(false);
  switch (row.type) {
    case "flag":
      return (
        <input
          type="checkbox"
          className="h-4 w-4 accent-[var(--color-accent)]"
          checked={value === true}
          aria-label={label}
          data-testid="server-setting-input"
          onChange={(e) => onChange(e.target.checked === row.value ? undefined : e.target.checked)}
        />
      );
    case "choice":
      return (
        <select className="kss-control kss-field bg-transparent px-2 py-1 text-sm" value={String(value)} aria-label={label} data-testid="server-setting-input" onChange={(e) => onChange(e.target.value === row.value ? undefined : e.target.value)}>
          {row.choices.map((c) => (
            <option key={c} value={c}>
              {CHOICE[row.key]?.[c] ? t(CHOICE[row.key]![c]!) : c}
            </option>
          ))}
        </select>
      );
    case "int":
    case "float":
      return (
        <Input
          type="number"
          className="w-40"
          value={String(value)}
          min={row.min ?? undefined}
          max={row.max ?? undefined}
          step={row.type === "int" ? 1 : "any"}
          aria-label={label}
          data-testid="server-setting-input"
          onChange={(e) => {
            const n = e.target.value === "" ? NaN : Number(e.target.value);
            onChange(Number.isNaN(n) || n === row.value ? undefined : n);
          }}
        />
      );
    case "secret":
      if (draft !== undefined || replacing)
        return (
          <span className="flex items-center gap-2">
            <Input
              type="password"
              autoComplete="new-password"
              className="w-64"
              value={String(draft ?? "")}
              placeholder={t("server.secret.new")}
              aria-label={label}
              data-testid="server-setting-input"
              onChange={(e) => onChange(e.target.value)}
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setReplacing(false);
                onChange(undefined);
              }}
            >
              {t("common.cancel")}
            </Button>
          </span>
        );
      return (
        <span className="flex items-center gap-2">
          <span className="text-sm" data-testid="server-secret-state">
            {value ? t("server.secret.set") : t("server.secret.notSet")}
          </span>
          <Button type="button" size="sm" variant="secondary" onClick={() => setReplacing(true)} data-testid="server-secret-replace">
            {t("server.secret.replace")}
          </Button>
          {value && (
            <Button type="button" size="sm" variant="ghost" onClick={() => onChange("")} data-testid="server-secret-clear">
              {t("server.secret.clear")}
            </Button>
          )}
        </span>
      );
    default:
      return (
        <Input
          type={row.type === "url" ? "url" : "text"}
          className="w-full max-w-md font-mono text-xs"
          value={String(value)}
          aria-label={label}
          data-testid="server-setting-input"
          onChange={(e) => onChange(e.target.value === row.value ? undefined : e.target.value)}
        />
      );
  }
}

function TestConnection({ target, rows, draft }: { target: "llm" | "decision"; rows: SettingRow[]; draft: Draft }) {
  const t = useT();
  const test = useLlmTest();
  const prefix = target === "llm" ? "KM_LLM_" : "KM_DECISION_";
  const pick = (k: string) => {
    const key = prefix + k;
    return key in draft ? String(draft[key]) : String(rows.find((r) => r.key === key)?.value ?? "");
  };
  const keyDraft = draft[`${prefix}API_KEY`];
  return (
    <div className="flex max-w-xs flex-col items-end gap-1 text-right">
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={test.isPending}
        data-testid="server-test-connection"
        onClick={() => test.mutate({ target, baseUrl: pick("BASE_URL"), model: pick("MODEL"), apiKey: target === "llm" && keyDraft !== undefined ? String(keyDraft) : undefined })}
      >
        <PlugZap size={14} aria-hidden="true" />
        {test.isPending ? t("server.test.running") : t("server.test")}
      </Button>
      {test.isPending && <span className="text-xs text-neutral-500">{t("server.test.patience")}</span>}
      {test.data && <TestResult result={test.data} />}
      {test.isError && <span className="text-xs text-err">{t("server.needServer")}</span>}
    </div>
  );
}

function TestResult({ result }: { result: LlmTestResult }) {
  const t = useT();
  return (
    <span className={clsx("text-xs", result.ok ? "text-ok" : "text-err")} role="status" data-testid="server-test-result" data-ok={result.ok} data-detail={result.detail}>
      {t(TEST[result.detail] ?? result.detail, { model: result.model ?? "", ms: String(result.latencyMs ?? ""), message: result.message })}
    </span>
  );
}

function Environment({ data }: { data: ServerSettings }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <section className="kss-surface mb-4 p-4" data-testid="server-environment">
      <button type="button" className="flex w-full items-center gap-2 text-left" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="server-environment-toggle">
        {open ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
        <span className="text-[17px] font-semibold">{t("server.environment")}</span>
      </button>
      <p className="ml-6 text-[13px] text-neutral-500">{t("server.environment.sub")}</p>
      {open && (
        <dl className="ml-6 mt-3 grid grid-cols-[minmax(0,14rem)_1fr] gap-x-4 gap-y-1.5 text-sm">
          {data.environment.map((e) => (
            <div key={e.key} className="contents">
              <dt className="truncate font-mono text-xs text-neutral-500">{e.key}</dt>
              <dd className="min-w-0 break-all font-mono text-xs">{e.value || "—"}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}
